import React from "react";
import { useState, useEffect, useRef, useCallback } from 'react';
import { STORE_OPTIONS } from '../../../constants/stores';
import {
  createBread,
  updateBread,
  disableBread,
  enableBread,
  uploadBreadImage,
  getBreadAuditLog,
  setCategoryMeta,
} from '../../../services/breadCatalog.service';

// ── Helpers ────────────────────────────────────────────────────────

function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `hace ${days}d`;
  return date.toLocaleDateString('es-MX', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFieldLabel(field) {
  const labels = {
    name_es: 'Nombre (ES)',
    name_en: 'Name (EN)',
    price: 'Precio',
    category: 'Categoría',
    enabled: 'Estado',
    image_url: 'Imagen',
    disabled_store_ids: 'Tiendas',
    tags: 'Etiquetas',
    description_es: 'Descripción (ES)',
    description_en: 'Description (EN)',
    min_qty: 'Cantidad mínima',
    slug: 'Slug',
  };
  return labels[field] || field;
}

function formatValue(field, value) {
  if (value === null || value === undefined) return '—';
  if (field === 'price') return `$${Number(value).toFixed(2)}`;
  if (field === 'enabled') return value ? 'Activo' : 'Inactivo';
  if (Array.isArray(value)) return value.join(', ') || '—';
  return String(value);
}

// ── Component ──────────────────────────────────────────────────────

export default function BreadCatalogDetail({
  bread, // null = create mode
  categoryMeta,
  onBack,
  onSaved,
  onToast,
}) {
  const isNew = !bread;
  const fileInputRef = useRef(null);

  // Side sheet open animation state
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Trigger slide-in on mount
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsOpen(true));
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => onBack(), 300); // wait for slide-out animation
  }, [onBack]);

  // ── Form State ─────────────────────────────────────────────────
  const [form, setForm] = useState({
    name_es: '',
    name_en: '',
    plu: '',
    category: '',
    image_url: '',
    enabled: true,
    disabled_store_ids: [],
    description_es: '',
    description_en: '',
    min_qty: '',
    tags: [],
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatNameEs, setNewCatNameEs] = useState('');
  const [newCatNameEn, setNewCatNameEn] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('📁');
  const [newTagInput, setNewTagInput] = useState('');

  // Initialize form from bread
  useEffect(() => {
    if (bread) {
      setForm({
        name_es: bread.name_es || '',
        name_en: bread.name_en || '',
        plu: bread.plu || bread.id || '',
        category: bread.category || '',
        image_url: bread.image_url || '',
        enabled: bread.enabled ?? true,
        disabled_store_ids: bread.disabled_store_ids || [],
        description_es: bread.description_es || '',
        description_en: bread.description_en || '',
        min_qty: bread.min_qty != null ? String(bread.min_qty) : '',
        tags: bread.tags || [],
      });
      setDirty(false);

      // Load audit log
      getBreadAuditLog(bread.id, 20)
        .then(setAuditLog)
        .catch((err) => console.error('Error loading audit:', err));
    }
  }, [bread]);

  // ── Handlers ───────────────────────────────────────────────────

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name_es.trim()) errs.name_es = 'El nombre es obligatorio';
    if (!form.name_en.trim()) errs.name_en = 'El nombre en inglés es obligatorio';
    if (!form.category) errs.category = 'Selecciona una categoría';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      const trimmedMinQty = String(form.min_qty ?? '').trim();
      const parsedMinQty = trimmedMinQty === '' ? null : parseInt(trimmedMinQty, 10);
      if (trimmedMinQty !== '' && (Number.isNaN(parsedMinQty) || parsedMinQty < 1)) {
        setErrors((prev) => ({ ...prev, min_qty: 'Cantidad mínima inválida' }));
        setSaving(false);
        return;
      }

      const data = {
        name_es: form.name_es.trim(),
        name_en: form.name_en.trim(),
        plu: form.plu.trim() || null,
        category: form.category,
        image_url: form.image_url || null,
        enabled: form.enabled,
        disabled_store_ids: form.disabled_store_ids,
        description_es: form.description_es.trim() || null,
        description_en: form.description_en.trim() || null,
        min_qty: parsedMinQty,
        tags: form.tags,
      };

      if (isNew) {
        await createBread(data);
        onToast('✓ Pan creado');
      } else {
        await updateBread(bread.id, data);
        onToast('✓ Guardado');
      }

      setDirty(false);
      onSaved();
    } catch (err) {
      console.error('Error saving bread:', err);
      onToast('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (dirty && !window.confirm('¿Descartar los cambios sin guardar?')) return;
    handleClose();
  };

  const handleDisable = async () => {
    if (!bread) return;
    if (!window.confirm('¿Desactivar este pan? Dejará de aparecer en todas las tiendas.')) return;
    setSaving(true);
    try {
      await disableBread(bread.id);
      onToast('Pan desactivado');
      onSaved();
    } catch (err) {
      console.error('Error disabling bread:', err);
      onToast('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    if (!bread) return;
    setSaving(true);
    try {
      await enableBread(bread.id);
      onToast('Pan activado');
      onSaved();
    } catch (err) {
      console.error('Error enabling bread:', err);
      onToast('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Image Upload ───────────────────────────────────────────────

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onToast('Solo se permiten imágenes');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      onToast('La imagen no debe superar 5MB');
      return;
    }

    setUploading(true);
    try {
      const id = bread?.id || 'temp_' + Date.now();
      const url = await uploadBreadImage(id, file);
      updateField('image_url', url);
      onToast('✓ Imagen subida');
    } catch (err) {
      console.error('Error uploading image:', err);
      onToast('Error al subir imagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
    // Reset file input so same file can be selected again & cancel doesn't break
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImageFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // ── Store Toggles ──────────────────────────────────────────────

  const toggleStore = (storeValue) => {
    const disabled = form.disabled_store_ids;
    if (disabled.includes(storeValue)) {
      updateField('disabled_store_ids', disabled.filter((s) => s !== storeValue));
    } else {
      updateField('disabled_store_ids', [...disabled, storeValue]);
    }
  };

  const storeStatusText = () => {
    const disabledCount = form.disabled_store_ids.length;
    if (disabledCount === 0) return { text: 'Activo en todas las tiendas', dot: 'all' };
    if (disabledCount >= STORE_OPTIONS.length) return { text: 'Inactivo en todas las tiendas', dot: 'none' };
    return { text: `Inactivo en ${disabledCount} tienda${disabledCount > 1 ? 's' : ''}`, dot: 'some' };
  };

  // ── Tags ───────────────────────────────────────────────────────

  const addTag = () => {
    const tag = newTagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      updateField('tags', [...form.tags, tag]);
    }
    setNewTagInput('');
  };

  const removeTag = (tag) => {
    updateField('tags', form.tags.filter((t) => t !== tag));
  };

  // ── New Category ───────────────────────────────────────────────

  const handleCreateCategory = async () => {
    if (!newCatNameEs.trim()) return;
    const key = newCatNameEs
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '_');

    try {
      await setCategoryMeta(key, {
        name_es: newCatNameEs.trim(),
        name_en: newCatNameEn.trim() || newCatNameEs.trim(),
        display_order: Object.keys(categoryMeta).length + 1,
        icon: newCatIcon || '📁',
        price: 0,
      });
      updateField('category', key);
      setShowNewCategory(false);
      setNewCatNameEs('');
      setNewCatNameEn('');
      setNewCatIcon('📁');
      onToast('✓ Categoría creada');
      onSaved();
    } catch (err) {
      console.error('Error creating category:', err);
      onToast('Error: ' + err.message);
    }
  };

  // ── Category Options ───────────────────────────────────────────

  const categoryOptions = Object.entries(categoryMeta)
    .sort(([, a], [, b]) => (a.display_order ?? 99) - (b.display_order ?? 99));

  const storeStatus = storeStatusText();
  const visibleAudit = auditExpanded ? auditLog : auditLog.slice(0, 5);

  const breadName = form.name_es || (isNew ? 'Nuevo Pan' : 'Pan');

  return (
    <>
      {/* Overlay */}
      <div
        className={`bc-sheet-overlay ${isOpen ? 'open' : ''}`}
        onClick={handleClose}
      />

      {/* Side Sheet */}
      <div className={`bc-sheet ${isOpen ? 'open' : ''}`}>
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="bc-sheet-header">
          <div className="bc-sheet-header-left" onClick={handleClose} role="button" tabIndex={0}>
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="bc-sheet-title">
              {isNew ? 'Nuevo Pan' : `Editar ${breadName}`}
            </span>
          </div>
          <button className="bc-sheet-close-btn" onClick={handleClose} type="button">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* ── Scrollable Content ──────────────────────────────── */}
        <div className="bc-sheet-body">

          {/* IMAGEN DEL PRODUCTO */}
          <div className="bc-sheet-section-label">Imagen del Producto</div>
          <div
            className={`bc-sheet-image-upload ${form.image_url ? 'has-image' : ''}`}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={handleImageDrop}
            onDragOver={handleDragOver}
          >
            {form.image_url ? (
              <>
                <img src={form.image_url} alt="Preview" />
                <div className="bc-sheet-image-overlay">
                  {uploading ? 'Subiendo...' : 'Cambiar imagen'}
                </div>
              </>
            ) : (
              <div className="bc-sheet-image-placeholder">
                <span className="material-symbols-outlined bc-sheet-upload-icon">cloud_upload</span>
                <span className="bc-sheet-upload-text">Arrastra o selecciona una foto</span>
                <span className="bc-sheet-upload-hint">JPG, PNG o WEBP (Máx. 5MB)</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />

          </div>
          {form.image_url && (
            <button
              className="bc-sheet-remove-image"
              onClick={(e) => { e.stopPropagation(); updateField('image_url', ''); }}
              type="button"
            >
              Eliminar imagen
            </button>
          )}

          {/* INFORMACIÓN GENERAL */}
          <div className="bc-sheet-section-label">Información General</div>

          <div className="bc-sheet-field">
            <label className="bc-sheet-field-label">Nombre del Producto <span className="bc-required">*</span></label>
            <input
              className={`bc-sheet-input ${errors.name_es ? 'error' : ''}`}
              type="text"
              value={form.name_es}
              onChange={(e) => updateField('name_es', e.target.value)}
              placeholder="Concha de Vainilla"
            />
            {errors.name_es && <div className="bc-field-error">{errors.name_es}</div>}
          </div>

          <div className="bc-sheet-field">
            <label className="bc-sheet-field-label">Product Name (EN) <span className="bc-required">*</span></label>
            <input
              className={`bc-sheet-input ${errors.name_en ? 'error' : ''}`}
              type="text"
              value={form.name_en}
              onChange={(e) => updateField('name_en', e.target.value)}
              placeholder="Vanilla Concha"
            />
            {errors.name_en && <div className="bc-field-error">{errors.name_en}</div>}
          </div>

          <div className="bc-sheet-row">
            <div className="bc-sheet-field" style={{ flex: '0 0 35%' }}>
              <label className="bc-sheet-field-label">PLU</label>
              <input
                className="bc-sheet-input"
                type="text"
                value={form.plu}
                onChange={(e) => updateField('plu', e.target.value)}
                placeholder={bread?.id || 'Auto'}
              />
            </div>
            <div className="bc-sheet-field" style={{ flex: 1 }}>
              <label className="bc-sheet-field-label">Categoría <span className="bc-required">*</span></label>
              <select
                className={`bc-sheet-input bc-sheet-select ${errors.category ? 'error' : ''}`}
                value={form.category}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    setShowNewCategory(true);
                  } else {
                    updateField('category', e.target.value);
                    setShowNewCategory(false);
                  }
                }}
              >
                <option value="">— Seleccionar —</option>
                {categoryOptions.map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.icon || ''} {meta.name_es}
                  </option>
                ))}
                <option value="__new__">+ Nueva categoría</option>
              </select>
              {errors.category && <div className="bc-field-error">{errors.category}</div>}
            </div>
          </div>

          {showNewCategory && (
            <div className="bc-new-category-form">
              <input
                className="bc-sheet-input"
                type="text"
                placeholder="Nombre en español"
                value={newCatNameEs}
                onChange={(e) => setNewCatNameEs(e.target.value)}
              />
              <input
                className="bc-sheet-input"
                type="text"
                placeholder="English name"
                value={newCatNameEn}
                onChange={(e) => setNewCatNameEn(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  className="bc-sheet-input"
                  type="text"
                  placeholder="🍞"
                  value={newCatIcon}
                  onChange={(e) => setNewCatIcon(e.target.value)}
                  style={{ width: '50px', flex: 'none' }}
                />
                <button
                  className="bc-sheet-btn-small"
                  onClick={handleCreateCategory}
                  type="button"
                >
                  Crear
                </button>
              </div>
            </div>
          )}

          {/* Descriptions */}
          <div className="bc-sheet-field">
            <label className="bc-sheet-field-label">Descripción (ES)</label>
            <textarea
              className="bc-sheet-input bc-sheet-textarea"
              value={form.description_es}
              onChange={(e) => updateField('description_es', e.target.value)}
              placeholder="Opcional"
              rows={2}
            />
          </div>
          <div className="bc-sheet-field">
            <label className="bc-sheet-field-label">Description (EN)</label>
            <textarea
              className="bc-sheet-input bc-sheet-textarea"
              value={form.description_en}
              onChange={(e) => updateField('description_en', e.target.value)}
              placeholder="Optional"
              rows={2}
            />
          </div>

          <div className="bc-sheet-field">
            <label className="bc-sheet-field-label">Cantidad mínima de orden</label>
            <input
              className={`bc-sheet-input ${errors.min_qty ? 'error' : ''}`}
              type="number"
              min="1"
              inputMode="numeric"
              value={form.min_qty}
              onChange={(e) => updateField('min_qty', e.target.value)}
              placeholder="Sin mínimo"
            />
            {errors.min_qty && <div className="bc-field-error">{errors.min_qty}</div>}
          </div>

          {/* DISPONIBILIDAD */}
          <div className="bc-sheet-section-label">Disponibilidad</div>
          <div className="bc-sheet-availability-card">
            {/* A la Venta toggle */}
            <div className="bc-sheet-toggle-row">
              <div className="bc-sheet-toggle-info">
                <span className="bc-sheet-toggle-title">A la Venta</span>
                <span className="bc-sheet-toggle-subtitle">Visible para los clientes en el menú</span>
              </div>
              <label className="bc-sheet-toggle">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => updateField('enabled', e.target.checked)}
                />
                <span className="bc-sheet-toggle-track" />
              </label>
            </div>
          </div>

          {/* Store Availability */}
          <div className="bc-sheet-section-label">Tiendas</div>
          <div className="bc-store-status">
            <span className={`bc-store-status-dot ${storeStatus.dot}`} />
            {storeStatus.text}
          </div>
          <div className="bc-store-list">
            {STORE_OPTIONS.map((store) => {
              const isEnabled = !form.disabled_store_ids.includes(store.value);
              return (
                <label key={store.value} className="bc-store-toggle">
                  <input
                    type="checkbox"
                    className="bc-store-checkbox"
                    checked={isEnabled}
                    onChange={() => toggleStore(store.value)}
                  />
                  <span className="bc-store-label">{store.label}</span>
                </label>
              );
            })}
          </div>

          {/* Tags */}
          <div className="bc-sheet-section-label" style={{ marginTop: '1.25rem' }}>Etiquetas</div>
          <div className="bc-tags">
            {form.tags.map((tag) => (
              <span key={tag} className="bc-tag">
                {tag}
                <button
                  className="bc-tag-remove"
                  onClick={() => removeTag(tag)}
                  type="button"
                >
                  ×
                </button>
              </span>
            ))}
            <div className="bc-tag-add">
              <input
                className="bc-tag-input"
                type="text"
                placeholder="+ agregar"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                }}
                onBlur={addTag}
              />
            </div>
          </div>

          {/* Audit Timeline */}
          {!isNew && auditLog.length > 0 && (
            <>
              <div className="bc-sheet-section-label" style={{ marginTop: '1.25rem' }}>Historial de Cambios</div>
              <div className="bc-audit-timeline">
                {visibleAudit.map((entry) => (
                  <div key={entry.id} className="bc-audit-entry">
                    <div className="bc-audit-dot" />
                    <div className="bc-audit-content">
                      <div className="bc-audit-time">{formatRelativeTime(entry.timestamp)}</div>
                      <div className="bc-audit-user">
                        {entry.user_email} ({entry.user_role})
                      </div>
                      <div className="bc-audit-changes">
                        {entry.action === 'created' ? (
                          <span>Creado</span>
                        ) : (
                          Object.entries(entry.changes).map(([field, change]) => (
                            <span key={field} className="bc-audit-change-line">
                              {formatFieldLabel(field)}:{' '}
                              <span className="bc-audit-old">{formatValue(field, change.old)}</span>
                              {' → '}
                              <span className="bc-audit-new">{formatValue(field, change.new)}</span>
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {auditLog.length > 5 && !auditExpanded && (
                <button
                  className="bc-audit-more"
                  onClick={() => setAuditExpanded(true)}
                  type="button"
                >
                  Ver historial completo ({auditLog.length} cambios) →
                </button>
              )}
            </>
          )}

          {/* Disable / Enable */}
          {!isNew && (
            <div className="bc-sheet-danger-zone">
              {bread.enabled ? (
                <button
                  className="bc-disable-btn"
                  onClick={handleDisable}
                  disabled={saving}
                  type="button"
                >
                  🚫 Desactivar Pan
                </button>
              ) : (
                <button
                  className="bc-enable-btn"
                  onClick={handleEnable}
                  disabled={saving}
                  type="button"
                >
                  ✅ Reactivar Pan
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Bottom Actions (sticky) ─────────────────────────── */}
        <div className="bc-sheet-footer">
          <button
            className="bc-sheet-save-btn"
            onClick={handleSave}
            disabled={saving || (!dirty && !isNew)}
            type="button"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <button
            className="bc-sheet-discard-btn"
            onClick={handleDiscard}
            type="button"
          >
            Descartar
          </button>
        </div>
      </div>
    </>
  );
}
